import { useState } from 'react';

import { submitAiAnswerFeedback } from '../../services/meetingApi';

// Stable reason codes (backend/models/ai_answer_feedback.py's
// FEEDBACK_REASON_CODES) with the labels shown to the user.
const REASONS = [
  { code: 'incorrect', label: 'Incorrect answer' },
  { code: 'not_relevant', label: 'Not relevant' },
  { code: 'too_long', label: 'Too long', reusable: 'Use shorter answers from now on' },
  { code: 'too_short', label: 'Too short', reusable: 'Use more detailed answers from now on' },
  { code: 'unclear', label: 'Difficult to understand', reusable: 'Use simpler explanations from now on' },
  { code: 'unnatural', label: 'Sounds unnatural', reusable: 'Use the Natural speaking style from now on' },
  { code: 'wrong_language', label: 'Wrong language' },
  { code: 'wrong_format', label: 'Wrong format' },
  { code: 'wrong_project_info', label: 'Wrong project information' },
  { code: 'other', label: 'Other' },
];

// Where "fix it for this answer" lives: the correction box under the answer
// regenerates THIS turn using the user's note. A rating never regenerates.
const NEEDS_CORRECTION = new Set(['incorrect', 'wrong_format', 'wrong_project_info', 'not_relevant']);

// What "use this from now on" saved (backend/services/feedback_preferences.py):
// a legacy answer setting, which meetings with an AI Response Style don't use -
// so never promise it reaches every future meeting.
const PREFERENCE_SAVED_MESSAGE =
  'Saved to your AI Answer Settings (Settings → AI Personalization → Additional AI Settings). '
  + 'Meetings that use an AI Response Style follow that style instead.';

// Live answers are written in the meeting's own AI Response Language
// (backend/routers/websocket.py); the account-level language a feedback
// shortcut could save is not read by them. So no shortcut is offered for
// "Wrong language" - only where the language really is chosen.
const WRONG_LANGUAGE_HELP =
  'Answers use this meeting\'s AI Response Language. Choose it when you start a meeting, '
  + 'or set the default for new meetings in Settings → Languages → AI Response Language.';

/**
 * Helpful / Not helpful for one COMPLETED answer. Saved against the answer
 * revision shown; the same button again clears it. No model call, never
 * regenerates, never interrupts the meeting - a failed save shows a short
 * message and nothing else changes. Remounted (keyed by the caller) when the
 * answer is regenerated, so a corrected answer starts unrated.
 */
export default function AnswerFeedback({ aiResponseId, answerRevision }) {
  const [rating, setRating] = useState(null);
  const [showReasons, setShowReasons] = useState(false);
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [reuse, setReuse] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const reason = REASONS.find((item) => item.code === reasonCode);

  async function send(feedback, extra = {}) {
    if (saving) return;
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const result = await submitAiAnswerFeedback(aiResponseId, {
        feedback,
        answerRevision,
        ...extra,
      });
      setRating(result?.feedback ?? null);
      if (result?.preference_applied) {
        setStatus(PREFERENCE_SAVED_MESSAGE);
      } else if (result?.feedback) {
        setStatus('Thanks for the feedback.');
      }
      return result;
    } catch (caught) {
      setError('Could not save your feedback. Try again.');
    } finally {
      setSaving(false);
    }
    return null;
  }

  function onHelpful() {
    setShowReasons(false);
    void send('like');
  }

  function onNotHelpful() {
    if (rating === 'dislike') {
      void send('dislike'); // same rating again clears it
      setShowReasons(false);
    } else {
      setShowReasons(true);
    }
  }

  async function onSubmitReason() {
    const extra = {
      reasonCode: reasonCode || 'other',
      ...(note.trim() ? { reason: note.trim().slice(0, 2000) } : {}),
      ...(reuse && reason?.reusable ? { applyPreference: true } : {}),
    };
    const result = await send('dislike', extra);
    if (result) setShowReasons(false);
  }

  return (
    <div className="copilot-feedback" aria-label="Answer feedback">
      <span className="copilot-label">Was this answer helpful?</span>
      <div className="copilot-actions">
        <button
          type="button"
          className={`copilot-feedback-btn${rating === 'like' ? ' is-active' : ''}`}
          aria-pressed={rating === 'like'}
          aria-label="Mark this answer as helpful"
          disabled={saving}
          onClick={onHelpful}
        >
          <span aria-hidden="true">👍</span> Helpful
        </button>
        <button
          type="button"
          className={`copilot-feedback-btn${rating === 'dislike' ? ' is-active' : ''}`}
          aria-pressed={rating === 'dislike'}
          aria-label="Mark this answer as not helpful"
          disabled={saving}
          onClick={onNotHelpful}
        >
          <span aria-hidden="true">👎</span> Not helpful
        </button>
        {saving && <span aria-live="polite">Saving…</span>}
        {status && !saving && <span aria-live="polite">{status}</span>}
        {error && (
          <span role="alert" style={{ color: 'var(--color-danger)' }}>
            {error}
          </span>
        )}
      </div>

      {showReasons && (
        <div className="copilot-correction-box">
          <label className="copilot-label" htmlFor="copilot-feedback-reason">
            What went wrong?
          </label>
          <select
            id="copilot-feedback-reason"
            className="copilot-correction-input"
            value={reasonCode}
            onChange={(event) => {
              setReasonCode(event.target.value);
              setReuse(false);
            }}
          >
            <option value="">Choose a reason (optional)</option>
            {REASONS.map((item) => (
              <option key={item.code} value={item.code}>
                {item.label}
              </option>
            ))}
          </select>

          <textarea
            className="copilot-correction-input"
            rows={2}
            maxLength={500}
            value={note}
            aria-label="Add a note (optional)"
            placeholder="Add a short note (optional)"
            onChange={(event) => setNote(event.target.value)}
          />

          {NEEDS_CORRECTION.has(reasonCode) && (
            <p className="copilot-answer-note">
              To fix this answer, use &ldquo;Not right? Correct it&rdquo; below - it regenerates
              this answer using your note.
            </p>
          )}

          {reason?.reusable && (
            <label className="copilot-feedback-reuse">
              <input type="checkbox" checked={reuse} onChange={(event) => setReuse(event.target.checked)} />{' '}
              {reason.reusable}
            </label>
          )}
          {reasonCode === 'wrong_language' && (
            <p className="copilot-answer-note">{WRONG_LANGUAGE_HELP}</p>
          )}

          <div className="copilot-actions">
            <button type="button" className="copilot-feedback-btn" disabled={saving} onClick={onSubmitReason}>
              Send feedback
            </button>
            <button type="button" className="copilot-feedback-btn" onClick={() => setShowReasons(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
